import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { fetchBotResponse, fetchChunkReference } from '../../services/api/KaiConfigData';
import { FaRobot } from "react-icons/fa";
import { lightenColor } from "../../constants/lightenColor";
import { isLightColor } from "../../utils/LuminosityChecker";
import { darkenColor } from "../../constants/darkenColor";
import { getSafeImageSrc } from '../../utils/imageUtils';

// Import the split components
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import ReferenceModal from '../ReferenceModal';
import { Message, ReferenceItem, ChatProps } from '../../types/types';

declare global {
  interface Window {
    MathJax?: {
      typeset?: (elements?: Array<HTMLElement>) => void;
      typesetPromise?: (elements?: Array<HTMLElement>) => Promise<any>;
      startup?: {
        promise?: Promise<any>;
      };
      tex?: any;
      svg?: any;
      options?: any;
      [key: string]: any;
    }
  }
}

const Chat: React.FC<ChatProps> = ({ 
  configData, 
  messages, 
  activeChatId, 
  onSendMessage, 
  isSidebarOpen, 
  pendingMsgCount, 
  allowSendMsg 
}) => {
  const [input, setInput] = useState("");
  const [Mobile, setIsMobile] = useState(false);
  const { app_id } = useParams(); 
  const [netId, setNetId] = useState(localStorage.getItem('net_id'));
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<Map<string, Message[]>>(new Map());
  
  // Ref to the last message
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [isNewChat, setIsNewChat] = useState(false);
  const [typingChatId, setTypingChatId] = useState<string | null>(null);
  const scrollButtonRef = useRef<HTMLButtonElement | null>(null);

  // Reference modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingChunks, setIsLoadingChunks] = useState(false);
  const [reference, setReference] = useState<ReferenceItem[]>([]);

  const [hasTyped, setHasTyped] = useState(false);
  const [safeAiIconSrc, setSafeAiIconSrc] = useState<string>(configData.app_ai_settings.ai_face);
  const [showScrollButton, setShowScrollButton] = useState<Map<string, boolean>>(new Map());

  const loadingIndicatorRef = useRef<HTMLDivElement | null>(null);

  const app_ai_settings = configData && configData?.app_ai_settings;

  // Update safe source when app_ai_face changes
  useEffect(() => {
    const updateSafeSource = async (): Promise<void> => {
      const src = configData.app_ai_settings.ai_face;
      const safeSrc = await getSafeImageSrc(src);
      setSafeAiIconSrc(safeSrc);
    };
    updateSafeSource();
  }, [configData.app_ai_settings.ai_face]);

  // Handle chunk reference
  const handleChunkReference = async (chatid: string) => {
    setIsModalOpen(true);
    setIsLoadingChunks(true);
    setReference([]);

    try {
      const result = await fetchChunkReference(chatid);
      const chatReferences = result?.data?.chat_references || result?.data || [];
      
      if (!chatReferences || chatReferences.length === 0) {
        setIsLoadingChunks(false);
        return;
      }

      const allChunkItems: ReferenceItem[] = chatReferences.map((referenceData: any, index: number) => {
        const meta = referenceData.chunk_metadata || referenceData.highest_scoring_chunk?.metadata || referenceData;
        
        return {
          chunk: meta.text || referenceData.highest_scoring_chunk?.text || '',
          file_name: meta.source_object_name || '',
          show: false,
          course_name: meta.source_parent_name || '',
          url: meta.source_object_url || '',
          source: meta.source || referenceData.source || '',
          source_category: meta.source_category || '',
        };
      }).filter((item: ReferenceItem) => item.chunk.trim() !== '');

      setReference(allChunkItems);
    } catch (error) {
      console.error('Error fetching chunk references:', error);
    } finally {
      setIsLoadingChunks(false);
    }
  };

  const toggleChunk = (index: number) => {
    setReference(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, show: !item.show } : item
      )
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  
  // Get bot response
  const getBotResponse = async (message: string) => {
    const result = await fetchBotResponse(app_id || '', netId || '', message, activeChatId); 
    const botResponse = `${result.data.response}`;
    return result;
  };

  // Update messages when new messages come in
  useEffect(() => {
    setChatMessages((prev) => {
      const updatedMessages = new Map(prev);
      updatedMessages.set(activeChatId, messages);
      return updatedMessages;
    });
  }, [messages, activeChatId]);

  useEffect(() => {
    if(typingChatId === 'chat'){
      setTypingChatId(activeChatId)
    }
  }, [typingChatId]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const scrollBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    if (scrollButtonRef.current) {
      scrollButtonRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const sendMessage = async (message: string) => {
    setHasTyped(false)
    if (!message.trim()) return;
    setIsNewChat(true);
    
    const newMessage: Message = { sender: 'user', message: message };
    setInput("");
    scrollToBottom();
    
    setChatMessages((prev) => {
      const updatedMessages = new Map(prev);
      const currentMessages = updatedMessages.get(activeChatId) || [];
      updatedMessages.set(activeChatId, [...currentMessages, newMessage]);
      return updatedMessages;
    });
  
    let chatIdFromResponse = '';
    onSendMessage(newMessage, activeChatId, chatIdFromResponse);
  
    setLoading(true);
    if(loading) scrollToBottom();
    
    try {
      let botResponse = await getBotResponse(message);
      const isRefAvailable = botResponse?.data.is_ref_available ?? false;
      const message_id = botResponse?.data.message_id ?? '';
      
      const botMessage: Message = { 
        sender: 'assistant', 
        message: botResponse?.data.response, 
        isRefAvailable,
        message_id: message_id
      };
      chatIdFromResponse = botResponse?.data.chat_id;
      
      let botMsg = botResponse?.data.response;
      
      setTypingChatId(activeChatId); 
      onSendMessage(botMessage, activeChatId, chatIdFromResponse);
    
      setChatMessages((prev) => {
        const updatedMessages = new Map(prev);
        const currentMessages = updatedMessages.get(activeChatId) || [];
        updatedMessages.set(activeChatId, [...currentMessages, botMessage]);
        return updatedMessages;
      });
      
      setTimeout(() => {
        scrollBottom();
      }, 100);
      
      setInput("");
      setTimeout(() => {
        setIsNewChat(false);
      }, botMsg.length * 50); 
    } catch (error) {
      console.error("Error fetching bot response:", error);
    } finally {
      setLoading(false);
      setInput("");
    }
  };

  // Scroll management
  useEffect(() => {
    const handleScroll = () => {
      if (chatContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        const atBottom = scrollHeight - scrollTop - clientHeight < 20;
        
        setShowScrollButton((prev) => {
          const updated = new Map(prev);
          updated.set(activeChatId, !atBottom);
          return updated;
        });
      }
    };

    const chatDiv = chatContainerRef.current;
    if (chatDiv) {
      chatDiv.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatDiv) {
        chatDiv.removeEventListener("scroll", handleScroll);
      }
    };
  }, [activeChatId]);

  const scrollToBottom = () => {
    chatContainerRef.current?.scrollTo({
      top: chatContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
    if (scrollButtonRef.current) {
      scrollButtonRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const handleScrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  useEffect(() => {
    handleScrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    let scrollInterval: any;
    
    if (loading) {
      scrollLoadingBottom();
      scrollInterval = setInterval(() => {
        scrollLoadingBottom();
      }, 500);
    }
    
    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [loading]);

  const scrollLoadingBottom = () => {
    if (loading && loadingIndicatorRef.current) {
      loadingIndicatorRef.current.scrollIntoView({ behavior: 'smooth', block: "end" });
    } else if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: "end" });
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [chatMessages]);

  // Color calculations
  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const isLight = isLightColor(backgroundColor);

  const lighterInputBoxBackground = isLight
    ? darkenColor(backgroundColor, 7)
    : lightenColor(backgroundColor, 7);
    
  const activeMessages = chatMessages.get(activeChatId) || [];
  const isMobile = window.innerWidth <= 640;
  const maxHeight = isMobile ? 'calc(100vh - 180px)' : 'calc(100vh - 120px)';

  return (
    <div className="flex flex-col h-screen w-full bg-gray-100 flex-col md:flex-row">
      <div className="flex-1 flex flex-col w-full" style={{ backgroundColor: configData.app_ui_settings.ui_backgroundcolor }}>
        
        {/* When no messages - Center everything */}
        {activeMessages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-4">
            {/* AI Icon */}
            <div className="mb-12">
              {safeAiIconSrc ? (
                <img
                  src={safeAiIconSrc}
                  alt="AI Face"
                  className="h-10 w-10 sm:h-14 sm:w-14 md:h-18 md:w-18"
                  style={{ color: configData.app_ui_settings.ui_iconcolor }}
                />
              ) : (
                <FaRobot
                  className="h-10 w-10 sm:h-14 sm:w-14 md:h-18 md:w-18"
                  style={{ color: configData.app_ui_settings.ui_fontcolor }}
                />
              )}
            </div>

            {/* Greeting */}
            {String(app_ai_settings.ai_showgreeting) === 'true' && (
              <h1
                className={`text-lg sm:text-xl md:text-xl text-center mb-8 max-w-2xl ${
                  app_ai_settings.ai_greeting && app_ai_settings.ai_greeting.length > 105 ? 'text-sm sm:text-lg md:text-xl' : ''
                }`}
                style={{ color: configData.app_ui_settings.ui_fontcolor }}
              >
                {app_ai_settings.ai_greeting
                  ? app_ai_settings.ai_greeting
                      .replace(/undefined/g, '')
                      .replace(/\s+/g, ' ')
                      .trim()
                  : ''
                }
              </h1>
            )}

            {/* Chat Input - Centered */}
            <ChatInput
              input={input}
              setInput={setInput}
              onSendMessage={sendMessage}
              configData={configData}
              loading={loading}
              pendingMsgCount={pendingMsgCount}
              allowSendMsg={allowSendMsg}
              messages={activeMessages}
              activeChatId={activeChatId}
              isSidebarOpen={isSidebarOpen}
            />
          </div>
        ) : (
          /* When messages exist - Normal layout */
          <>
            {/* Main Content */}
            <div
              id="previewContent"
              className="flex-1 p-2 lg:p-10 max-w-1xl md:max-w-1xl overflow-y-auto w-full lg:w-[100%] h-full pb-[5%] sm:pb-[5%] md:pb-[5%] lg:pb-[3%]"
              ref={chatContainerRef}
              style={{
                backgroundColor: configData.app_ui_settings.ui_backgroundcolor,
                color: configData.app_ui_settings.ui_fontcolor,
                maxHeight: maxHeight,
                scrollbarGutter: 'stable both-edges',
              }}
            >
              <MessageList
                messages={activeMessages}
                configData={configData}
                activeChatId={activeChatId}
                isNewChat={isNewChat}
                typingChatId={typingChatId}
                hasTyped={hasTyped}
                loading={loading}
                isSidebarOpen={isSidebarOpen}
                onScrollBottom={scrollBottom}
                onChunkReference={handleChunkReference}
                messagesEndRef={messagesEndRef}
                loadingIndicatorRef={loadingIndicatorRef}
                safeAiIconSrc={safeAiIconSrc}
              />

              {/* Scroll Down Button */}
              {showScrollButton.get(activeChatId) && (
                <button
                  ref={scrollButtonRef}
                  onClick={scrollToBottom}
                  className={`fixed left-[50%] bottom-40 transform -translate-x-1/2 bottom-20 p-3 rounded-full shadow-lg hover:bg-gray-700 transition-all flex items-center justify-center
                    ${isSidebarOpen ? 'ml-[5%] sm:ml-[5%] md:ml-[13%] lg:ml-[8%]' : 'ml-[1%] sm:ml-[1%] md:ml-[1%] lg:ml-[0%]'}
                  `}
                  style={{ 
                    zIndex: 50, 
                    color: configData.app_ui_settings.ui_fontcolor, 
                    background: lighterInputBoxBackground,
                  }}
                >
                  <ChevronDown size={15} />
                </button>
              )}
            </div>

            {/* Chat Input Component - Bottom */}
            <ChatInput
              input={input}
              setInput={setInput}
              onSendMessage={sendMessage}
              configData={configData}
              loading={loading}
              pendingMsgCount={pendingMsgCount}
              allowSendMsg={allowSendMsg}
              messages={activeMessages}
              activeChatId={activeChatId}
              isSidebarOpen={isSidebarOpen}
            />
          </>
        )}
      </div>

      {/* Reference Modal Component */}
      <ReferenceModal
        isModalOpen={isModalOpen}
        onClose={closeModal}
        isLoadingChunks={isLoadingChunks}
        reference={reference}
        configData={configData}
        onToggleChunk={toggleChunk}
      />
    </div>
  );
};

export default Chat;